import React from "react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, Info, Loader2 } from "lucide-react";

/**
 * Toaster — brand-native wrapper around Sonner.
 *
 * Design contract (see the redesign spec):
 *   • Surface: `bg-shell-side` (deep-navy sidebar token) + `text-shell-side-fg`
 *     across every variant. Success / error / warning / info differ ONLY
 *     by the leading icon color; the base card stays identical for
 *     brand consistency.
 *   • Shape / elevation: `rounded-xl` (matches the app's card radius
 *     token `--radius: 0.75rem`) + `shadow-2xl` for the "floating"
 *     read consistent with modals.
 *   • Bottom accent: 3px sky-400 (brand blue) border along the bottom
 *     edge — a subtle divider that differentiates the toast from a
 *     flat card without introducing a new color.
 *   • Close button: brand-pink `bg-primary` rounded pill so it never
 *     disappears into the dark background.
 *   • Sizing: `min-w-[320px]` up to `max-w-[420px]` on desktop; mobile
 *     styling below via `[data-mobile]` selector so the toast reads
 *     comfortably on 375 px screens (near-full-width with 16 px side
 *     margins).
 *   • Animation: Sonner's built-in slide+fade (200-250 ms) — we don't
 *     override it because the library's default already matches the
 *     spec and reuses its own easing token.
 *
 * Positioning is set at the mount point (`App.js` → `<Toaster
 * position="top-right" />`) — this file only owns styling + icons.
 */
const Toaster = (props) => {
  const { theme = "system" } = useTheme();
  // Expose the app's own `toast` singleton on `window` for QA
  // scripts / Playwright verification. Safe in production — it's the
  // same import path components already use, just a debug hook. No
  // side-effect if the browser doesn't consume it.
  React.useEffect(() => {
    try { window.__cpgToast = toast; } catch { /* ssr guard */ }
  }, []);
  return (
    <Sonner
      theme={theme}
      className="toaster group"
      // Custom icons per variant. The base surface stays the same for
      // all variants — differentiation comes from the leading icon
      // color, not the card background (per spec).
      icons={{
        success: <CheckCircle2 className="w-5 h-5 text-emerald-400" data-testid="toast-icon-success" />,
        error: <XCircle className="w-5 h-5 text-rose-400" data-testid="toast-icon-error" />,
        warning: <AlertTriangle className="w-5 h-5 text-pink-400" data-testid="toast-icon-warning" />,
        info: <Info className="w-5 h-5 text-sky-400" data-testid="toast-icon-info" />,
        loading: <Loader2 className="w-5 h-5 text-sky-400 animate-spin" data-testid="toast-icon-loading" />,
      }}
      toastOptions={{
        // NOTE: `unstyled` false keeps Sonner's own slide/fade
        // enter/exit animation and dismiss button positioning; we
        // override colors + shape via classNames without replacing
        // the library's motion system.
        classNames: {
          // Card. `!` prefixes beat Sonner's inline defaults for
          // background/border/shadow. `border-b-[3px] border-b-sky-400`
          // paints the brand-blue accent along the bottom; other
          // borders left as `border-transparent` so the card reads
          // clean on the shell.
          toast:
            "group toast font-sans " +
            "!bg-shell-side !text-shell-side-fg " +
            "!border !border-transparent !border-b-[3px] !border-b-sky-400 " +
            "!rounded-xl !shadow-2xl " +
            "!min-w-[320px] !max-w-[420px] " +
            "!px-5 !py-4 !text-[14px] !leading-snug " +
            // Mobile: take near-full-width so small screens don't
            // squish content into the 320 px floor.
            "max-[640px]:!w-[calc(100vw-32px)] max-[640px]:!min-w-0 max-[640px]:!max-w-none",
          // Title / description — reset default muted color so the
          // dark surface reads correctly.
          title: "text-[14px] font-semibold text-shell-side-fg",
          description: "text-[13px] text-shell-side-fg/70 mt-0.5",
          // Action / cancel buttons — respect brand-pink primary.
          actionButton:
            "group-[.toast]:!bg-primary group-[.toast]:!text-primary-foreground " +
            "!rounded-full !px-3 !py-1.5 !text-xs font-semibold",
          cancelButton:
            "group-[.toast]:!bg-white/10 group-[.toast]:!text-shell-side-fg " +
            "!rounded-full !px-3 !py-1.5 !text-xs font-semibold",
          // Close ("×") — brand-pink pill so it stays legible on the
          // deep-navy card. Sonner ships the SVG; we restyle its
          // wrapper button.
          closeButton:
            "!bg-primary !text-primary-foreground !border-primary " +
            "!rounded-md !w-6 !h-6 " +
            "hover:!bg-primary/90 focus-visible:!ring-2 focus-visible:!ring-primary/50",
          // Variant icon wrapper — Sonner renders `<div data-icon>`;
          // keep it aligned with the text and give it a fixed slot
          // so multi-line messages line up cleanly under the icon.
          icon: "!self-start !mt-0.5 shrink-0",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
